import React, { useState, useEffect } from "react";
import { register } from "../../../api";
import { AuthService } from "../../../services";

function SignupInfo({ onNext, onBack, stepData }) {
  const [form, setForm] = useState({
    nickname: "",
    password: "",
    confirmPassword: "",
    bongandangId: 0,
    name: stepData?.name || "", // Initialize with name from previous step
    email1: "",
    email2: "",
    emailDomain: "",
    sex: "남",
    birthday: "",
    phone1: "",
    handphone1: "010",
    handphone2: "",
    handphone3: "",
    postCode: "",
    address1: "",
    address2: ""
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [idCheckLoading, setIdCheckLoading] = useState(false);
  const [idCheckResult, setIdCheckResult] = useState("");
  const [places, setPlaces] = useState([]);
  const [isDuplicateChecked, setIsDuplicateChecked] = useState(false);

  // Compose email and phone fields for validation
  const email = `${form.email1}@${form.email2 || form.emailDomain}`;
  const phone = form.phone1;
  const handphone = `${form.handphone1}${form.handphone2}${form.handphone3}`;

  React.useEffect(() => {
    // Use hardcoded places list to match the correct options
    setPlaces([
      { id: 1, name: "가족묘" },
      { id: 2, name: "봉안담" },
      { id: 3, name: "봉안당 A관" },
      { id: 4, name: "봉안당 S관" },
      { id: 5, name: "봉안당 하늘관" },
      { id: 6, name: "봉안당 햇빛관" },
      { id: 7, name: "야외가족납골당" }
    ]);

    // Load Daum Postcode script
    if (!window.daum) {
      const script = document.createElement('script');
      script.src = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  // Debug useEffect to monitor idCheckResult changes
  React.useEffect(() => {
    console.log('Debug: idCheckResult changed to:', idCheckResult);
  }, [idCheckResult]);

  const handleIdCheck = async () => {
    if (!form.nickname.trim()) {
      setIdCheckResult("아이디를 입력해주세요.");
      return;
    }
    
    setIdCheckLoading(true);
    setIdCheckResult("");
    
    try {
      // API returns true if duplicate, false if available
      const isDuplicate = await AuthService.duplicateNicknameCheck({ nickname: form.nickname.trim() });
      
      if (isDuplicate) {
        setIdCheckResult("사용중인 아이디입니다. 다른 ID를 입력해주세요.");
        setIsDuplicateChecked(false);
      } else {
        setIdCheckResult("사용가능한 ID입니다.");
        setIsDuplicateChecked(true);
      }
    } catch (error) {
      setIdCheckResult("중복확인 중 오류가 발생했습니다. 다시 시도해주세요.");
      setIsDuplicateChecked(false);
    } finally {
      setIdCheckLoading(false);
    }
  };

  // Validation helpers
  const isPasswordValid = (pw) => {
    if (!pw) return false;
    if (pw.length < 6 || pw.length > 10) return false;
    // Allow alphanumeric and special characters
    const passwordRegex = /^[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+$/;
    return passwordRegex.test(pw);
  };
  
  const isPhoneValid = (phone) => {
    if (!phone) return true; // Optional field
    const cleanPhone = phone.replace(/-/g, "");
    return /^\d{8,10}$/.test(cleanPhone);
  };
  
  const isSexValid = (sex) => sex === "남" || sex === "여";
  
  // Helper function to check if phone contains combination of English letters and numbers
  const isAlphanumericCombination = (phone) => {
    // Must contain both letters and numbers, and only letters and numbers
    const hasLetter = /[a-zA-Z]/.test(phone);
    const hasNumber = /\d/.test(phone);
    const onlyAlphanumeric = /^[a-zA-Z0-9]+$/.test(phone);
    return hasLetter && hasNumber && onlyAlphanumeric;
  };
  
  const isEmailValid = (email) => {
    if (!email) return true; // Optional field
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  
  const isBirthdayValid = (birthday) => {
    if (!birthday) return true; // Optional field
    return /^\d{8}$/.test(birthday);
  };

  // Handle numeric input - only allow numbers
  const handleNumericChange = (e) => {
    const { name, value } = e.target;
    // Only allow numeric characters
    const numericValue = value.replace(/[^0-9]/g, '');
    setForm((prev) => ({ ...prev, [name]: numericValue }));
    
    // Clear error when user starts typing
    if (error) {
      setError("");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (error) {
      setError("");
    }
    
    if (name === 'nickname' && isDuplicateChecked) {
      setIsDuplicateChecked(false);
      setIdCheckResult("");
    }
    
    // Check for restricted names in both nickname and name fields
    const restrictedNames = ['ganghwa', 'saswat', 'riya', 'test'];
    const inputValue = value.toLowerCase().trim();
    
    if (restrictedNames.includes(inputValue)) {
      setError("사용할 수 없는 이름입니다. 다른 이름을 입력해주세요.");
    } else {
      setError(""); // Clear error if name is valid
    }
  };

  const handleEmailDomain = (e) => {
    const selectedValue = e.target.value;
    setForm((prev) => ({ 
      ...prev, 
      emailDomain: selectedValue,
      email2: (selectedValue === "직접 입력하기" || selectedValue === "선택해 주세요" || selectedValue === "") ? "" : selectedValue
    }));
  };

  const openPostcodePopup = () => {
    if (!window.daum || !window.daum.Postcode) {
      alert("주소 검색 서비스를 불러오는 중입니다. 잠시 후 다시 시도해주세요.");
      return;
    }
    new window.daum.Postcode({
      oncomplete: function(data) {
        // Fill postCode and address1 automatically
        // address2 will remain empty for user input only
        setForm(prev => ({
          ...prev,
          postCode: data.zonecode,
          address1: data.roadAddress || data.jibunAddress,
          address2: "" // Keep empty for user input only
        }));
      }
    }).open();
  };

  const showValidationAlert = (message) => {
    alert(message);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    // Form validation based on Java backend requirements - matching PC version
    // @NotBlank - User ID validation
    if (!form.nickname?.trim()) {
      showValidationAlert('아이디를 입력해주세요. (6~20자 영문, 숫자 사용 가능)');
      return;
    } else if (form.nickname.length < 6 || form.nickname.length > 20) {
      showValidationAlert('아이디를 입력해주세요. (6~20자 영문, 숫자 사용 가능)');
      return;
    } else if (!isDuplicateChecked) {
      showValidationAlert('아이디 중복확인을 해주세요.');
      return;
    }
    
    // @NotBlank + @Size(min = 6, max = 10) - Password validation
    if (!form.password) {
      showValidationAlert('비밀번호를 입력해주세요. (6~10자 영문, 숫자, 특수기호 사용 가능)');
      return;
    } else if (!isPasswordValid(form.password)) {
      showValidationAlert('비밀번호를 입력해주세요. (6~10자 영문, 숫자, 특수기호 사용 가능)');
      return;
    }
    
    // @NotBlank - Confirm password validation
    if (form.password && !form.confirmPassword) {
      showValidationAlert('비밀번호 확인을 해주세요.');
      return false;
    }
   if (form.password && form.confirmPassword && form.password !== form.confirmPassword) {
    showValidationAlert('비밀번호가 일치하지 않습니다.');
    return;
    }
    
   // 이메일 validation
   if (!form.email1 || !form.email1.trim()) {
    showValidationAlert('도메인을 입력해주세요.');
    return false;
  }

  if (!form.email2 && !form.emailDomain) {
    showValidationAlert('도메인을 입력해주세요.');
    return false;
  }


  // Final email format validation
  const email = `${form.email1}@${form.email2 || form.emailDomain}`;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    showValidationAlert('올바른 이메일 형식을 입력해주세요');
    return false;
  }


    if (!form.name.trim()) {
      showValidationAlert('이름(실명)을 입력해주세요.');
      return;
    }
    
    if (form.name.length < 2) {
      showValidationAlert('이름은 2자 이상 입력해주세요.');
      return;
    }
    
    // Korean characters only validation for name field
    const koreanRegex = /^[가-힣]+$/;
    if (!koreanRegex.test(form.name.trim())) {
      showValidationAlert('이름은 한글만 입력해주세요.');
      return;
    }
    
    // Birthday validation - must be empty or in YYYYMMDD format
    if (form.birthday && form.birthday.trim()) {
      if (!/^\d{8}$/.test(form.birthday.trim())) {
        showValidationAlert('생년월일은 8자리 숫자로 입력해주세요.');
        return;
      }
     
    }
    
    // Contact number validation (optional) - matching EditInfo.jsx pattern
    const contact = form.phone1;
    if (contact) {
      // Check length first
      if (contact.length > 10) {
        showValidationAlert('영문+숫자 10자리 미만');
        return;
      }
      
      // Check if it contains combination of English letters and numbers
      if (!isAlphanumericCombination(contact)) {
        showValidationAlert('영문+숫자 10자리 미만');
        return;
      }
    }
    
    // @NotBlank - Mobile phone validation (required field)
    const mobilePhone = `${form.handphone1}${form.handphone2}${form.handphone3}`;
    if (!form.handphone2 || !form.handphone3) {
      showValidationAlert('휴대전화번호를 입력하세요. (8~11자 숫자 사용 가능)')
      return;

    } else {
      const mobileRegex = /^\d{8,11}$/;
      if (!mobileRegex.test(mobilePhone)) {
        showValidationAlert('휴대전화번호를 입력하세요. (8~11자 숫자 사용 가능)')
        return;
      }
    }

    // @NotBlank - Address validation (required field)
    if (!form.postCode?.trim()) {
      showValidationAlert('우편번호를 입력해주세요.');
      return;
    }

    if (!form.address1?.trim()) {
      showValidationAlert('우편번호를 입력해주세요.');
      return;
    }

    if (!form.address2 || !form.address2.trim()) {
      showValidationAlert('상세주소를 입력해주세요.(5~50자)');
      return;
    }

    if (form.address2.length < 5 || form.address2.length > 50) {
      showValidationAlert('상세주소를 입력해주세요.(5~50자)');
      return;
    }
    
    // @NotBlank - Gender validation (required field)
    if (!form.sex) {
      showValidationAlert('성별을 선택해주세요.');
      return;
    }
    
    setLoading(true);
    const data = {
      nickname: form.nickname,
      password: form.password,
      confirmPassword: form.confirmPassword,
      bongandangId: Number(form.bongandangId) || 1,
      name: form.name,
      email,
      sex: form.sex,
      birthday: form.birthday?.trim() || "",
      phone: phone,
      handphone: mobilePhone,
      postCode: form.postCode,
      address1: form.address1,
      address2: form.address2
    };
    
    try {
      await register(data);
      onNext();
    } catch (error) {
      console.error('Registration error:', error);
      setError(error.response?.data?.message || '회원가입에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-info">
      <div className="info-title">*기본(필수)정보 입력</div>
      <form className="info-form" onSubmit={handleSubmit} style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ background: '#f7f7f7', padding: 16, borderRadius: 6, marginBottom: 18 }}>
          <div className="form-group">
            <label>아이디<span className="required-asterisk">*</span></label>
            <div className="mobile-id-check-container">
              <div className="mobile-id-input-row">
                <input type="text" name="nickname" value={form.nickname} onChange={handleChange} />
                <button type="button" className="mobile-id-check-btn" onClick={handleIdCheck} disabled={idCheckLoading || !form.nickname.trim()}>
                  {idCheckLoading ? "확인 중..." : "중복확인"}
                </button>
              </div>
              {idCheckResult && (
                <div className={`mobile-id-check-result${idCheckResult.includes('가능') ? ' success' : ' error'}`}>
                  *{idCheckResult}
                </div>
              )}
            </div>
          </div>
          <div className="form-group">
            <label>패스워드<span className="required-asterisk">*</span></label>
            <div className="password-div">
            <input type="password" name="password" value={form.password} onChange={handleChange} />
            <div className="info-msg">6~10자 영문, 숫자, 특수기호 사용 가능</div>
            </div>
          </div>
          <div className="form-group">
            <label>패스워드 확인<span className="required-asterisk">*</span></label>
            <input type="password" name="confirmPassword" value={form.confirmPassword} onChange={handleChange} />
            {form.password && form.confirmPassword && (
              <div className={`info-msg ${form.password === form.confirmPassword ? "success" : "error"}`}>
                {form.password === form.confirmPassword ? "*패스워드 일치" : "*패스워드 불일치"}
              </div>
            )}
          </div>
          <div className="form-group">
            <label>봉안당</label>
            <select name="bongandangId" value={form.bongandangId} onChange={handleChange}>
              {places.length === 0 ? (
                <option value="">로딩 중...</option>
              ) : (
                places.map((place) => (
                  <option key={place.id} value={place.id}>{place.name}</option>
                ))
              )}
            </select>
          </div>
          <div className="form-group">
            <label>이름(실명)<span className="required-asterisk">*</span></label>
            <input 
              type="text" 
              name="name" 
              value={form.name} 
              onChange={handleChange}
              autoComplete="name"
              placeholder="실명을 입력하세요"
              disabled
            />
          </div>
          <div className="form-group">
            <label>이메일 <span className="required-asterisk">*</span></label>
            <div className="mobile-form-email-row">
              <input type="text" name="email1" value={form.email1} onChange={handleChange} />
              <span>@</span>
              <input 
                type="text" 
                name="email2" 
                value={form.email2} 
                onChange={handleChange}
                disabled={form.emailDomain !== '선택해 주세요' && form.emailDomain !== '직접 입력하기' && form.emailDomain !== ''}
                className={form.emailDomain !== '선택해 주세요' && form.emailDomain !== '직접 입력하기' && form.emailDomain !== '' ? 'disabled-input' : ''}
              />
              <select name="emailDomain" value={form.emailDomain} onChange={handleEmailDomain}>
                <option value="">선택해 주세요</option>
                <option value="naver.com">naver.com</option>
                <option value="daum.net">daum.net</option>
                <option value="hanmail.com">hanmail.com</option>
                <option value="nate.com">nate.com</option>
                <option value="gmail.com">gmail.com</option>
                <option value="paran.com">paran.com</option>
                <option value="dreamwiz.com">dreamwiz.com</option>
                <option value="hotmail.com">hotmail.com</option>
                <option value="empal.com">empal.com</option>
                <option value="lycos.co.kr">lycos.co.kr</option>
                <option value="freechal.com">freechal.com</option>
                <option value="hanafos.com">hanafos.com</option>
                <option value="korea.com">korea.com</option>
                <option value="hanmir.com">hanmir.com</option>
                <option value="직접 입력하기">직접 입력하기</option>
              </select>
            </div>
          </div>
              <div className="form-group">
             <label>성별</label>
             <div className="radio-group">
               <fieldset className="gr-fieldset">
                 <legend className="gr-legend">성별</legend>

                 {/* 남 */}
                 <div className="gr-option">
                   <input
                     type="radio"
                     id="gender-male"
                     name="sex"
                     value="남"
                     checked={form.sex === '남'}
                     onChange={handleChange}
                     className="gr-input"
                   />
                   <label htmlFor="gender-male" className="gr-label">
                     <span style={{color: '#666666'}}>남</span>
                   </label>
                 </div>

                 {/* 여 */}
                 <div className="gr-option">
                   <input
                     type="radio"
                     id="gender-female"
                     name="sex"
                     value="여"
                     checked={form.sex === '여'}
                     onChange={handleChange}
                     className="gr-input"
                   />
                   <label htmlFor="gender-female" className="gr-label">
                     <span style={{color: '#666666'}}>여</span>
                   </label>
                 </div>
               </fieldset>
             </div>
           </div>
          <div className="form-group">
            <label>생년월일</label>
            <div className="birthday-row">
              <input 
               type="text" 
               name="birthday" 
               value={form.birthday} 
               onChange={handleNumericChange} 
               placeholder="예) 19800101" 
               inputMode="numeric"
               maxLength={8}
             />
          <div className="instruction-text">예) 19800101</div>
          </div>
          </div>
          <div className="form-group">
            <label>연락처</label>
            <input
              type="text"
              name="phone1"
              value={form.phone1}
              onChange={handleChange}
            />
            <div className="hint">영문+숫자 10자리 미만</div>
          </div>
          <div className="form-group">
            <label>휴대전화<span className="required-asterisk">*</span></label>
            <div className="contact-row">
              <div className="mobile-input-select">
              <select name="handphone1" value={form.handphone1} onChange={handleChange}>
                <option value="010">010</option>
                <option value="011">011</option>
                <option value="016">016</option>
                </select>
                </div>
              <span>-</span>
              <input 
                type="text" 
                name="handphone2" 
                value={form.handphone2} 
                onChange={handleNumericChange} 
                maxLength={4} 
                inputMode="numeric"
              />
              <span>-</span>
              <input 
                type="text" 
                name="handphone3" 
                value={form.handphone3} 
                onChange={handleNumericChange} 
                maxLength={4} 
                inputMode="numeric"
              />
            </div>
          </div>
          <div style={{display: 'flex', flexDirection: 'column', gap: '4x'}} className="form-group">
            <label>주소<span className="required-asterisk">*</span></label>
            <div className="address-row">
              <input 
                type="text" 
                name="postCode" 
                value={form.postCode} 
                onChange={handleNumericChange} 
                placeholder="우편번호" 
                inputMode="numeric"
              />
              <button type="button" className="check-btn" onClick={openPostcodePopup}>우편번호</button>
            </div>
            <input type="text" name="address1" value={form.address1} onChange={handleChange} placeholder="상세주소" />
             <input type="text" name="address2" value={form.address2} onChange={handleChange} placeholder="상세주소2" /> 
          </div>
          </div>
        {error && <div className="error-msg">{error}</div>}
        <div className="form-btns">
          <button 
            type="submit" 
            className="register-btn" 
            disabled={loading}
            onClick={() => console.log('Debug: Register button clicked')}
            style={{ 
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
              pointerEvents: loading ? 'none' : 'auto'
            }}
          >
            {loading ? "등록 중..." : "등록"}
          </button>
          <button type="button" className="mobile-cancel-btn" onClick={onBack} disabled={loading}>이전</button>

        </div>
      </form>
    </div>
  );
}

export default SignupInfo; 